import _, { debounce } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../framework/Button";
import GlobalContext from "../../../framework/GlobalContext";
import Shortcut from "../../../framework/Shortcut";
import Tab from "../../../framework/tab/Tab";
import {
  ADD_DISTRIBUTOR_ACCOUNT_TAB,
  ADD_DISTRIBUTOR_GENERAL_TAB,
  ADD_DISTRIBUTOR_LICENSE_TAB,
  ADD_DISTRIBUTOR_VISIT_TAB,
} from "../../../framework/tab/tabUtilsConstants";
import { displayToast } from "../../../framework/toast/toastUtils";
import { saveDistributor } from "../../../services/distributor-services";
import {
  checkErrorStateExists,
  focusInputField,
  isValidPhoneNumber,
  removeErrorState,
} from "../../../utils/utils";
import classes from "./index.module.css";
import { createDistributorFormData } from "./addDistributorUtils";
import { Typography } from "@mui/material";

const AddDistributor = ({
  distributorRowEditData = null,
  onSaveDistributor = () => {},
  onEscape = () => {},
}) => {
  const addDistributorSaveRef = useRef();
  const { setCurrentComponent, setShowLoading } = useContext(GlobalContext);
  const { formState, errorState } = useSelector(
    (state) => state.commonReducer.common.addDistributor
  );
  const dispatch = useDispatch();
  const dispatchErrorStates = useCallback(
    (payload) => {
      dispatch({ type: "set_add_distributor_error_state", payload });
    },
    [dispatch]
  );
  const removeErrorStateOnChange = useCallback(
    (id) => {
      let updatedErrorState = errorState;
      if (checkErrorStateExists(id, errorState)) {
        updatedErrorState = removeErrorState(id, updatedErrorState, errorState);
        dispatchErrorStates(updatedErrorState);
      }
    },
    [dispatchErrorStates, errorState]
  );

  const resetForm = useCallback(() => {
    dispatch({
      type: "reset_add_distributor_form_state",
    });
    dispatchErrorStates([]);
  }, [dispatch, dispatchErrorStates]);

  const validateForm = useCallback(
    (fieldName, data) => {
      const _formState = { ...data };
      let updatedErrorState = errorState;
      if (fieldName === "distributorName" || !fieldName) {
        let name = _formState?.distributorName?.trim();
        if (!name) {
          displayToast({
            type: "error",
            title: "Please enter a distributor name",
          });
          if (!checkErrorStateExists("add_distributor_name", errorState)) {
            updatedErrorState.push({ add_distributor_name: true });
          }
          dispatchErrorStates(updatedErrorState);
          if (!fieldName) {
            focusInputField(updatedErrorState);
          }
          return false;
        } else {
          if (checkErrorStateExists("add_distributor_name", errorState)) {
            updatedErrorState = removeErrorState(
              "add_distributor_name",
              updatedErrorState,
              errorState
            );
            dispatchErrorStates(updatedErrorState);
          }
        }
      }
      if (fieldName === "contact" || !fieldName) {
        let contact = _formState?.contact?.trim();
        if (!contact || contact.length < 10) {
          displayToast({
            type: "error",
            title: "Please enter a contact number",
          });
          if (!checkErrorStateExists("add_distributor_contact", errorState)) {
            updatedErrorState.push({ add_distributor_contact: true });
          }
          dispatchErrorStates(updatedErrorState);
          if (!fieldName) {
            focusInputField(updatedErrorState);
          }
          return false;
        } else {
          if (checkErrorStateExists("add_distributor_contact", errorState)) {
            updatedErrorState = removeErrorState(
              "add_distributor_contact",
              updatedErrorState,
              errorState
            );
            dispatchErrorStates(updatedErrorState);
          }
        }
      }
      if (fieldName === "address" || !fieldName) {
        const address = _formState?.address?.trim();
        if (!address) {
          displayToast({
            type: "error",
            title: "Please enter a address",
          });
          if (!checkErrorStateExists("add_distributor_address", errorState)) {
            updatedErrorState.push({ add_distributor_address: true });
          }
          dispatchErrorStates(updatedErrorState);
          if (!fieldName) {
            focusInputField(updatedErrorState);
          }
          return false;
        } else {
          if (checkErrorStateExists("add_distributor_address", errorState)) {
            updatedErrorState = removeErrorState(
              "add_distributor_address",
              updatedErrorState,
              errorState
            );
            dispatchErrorStates(updatedErrorState);
          }
        }
      }

      if (fieldName === "pincode" || !fieldName) {
        const pincode = _formState?.pincode;
        if (!pincode || pincode.toString().length !== 6) {
          displayToast({
            type: "error",
            title: "Please enter a pincode",
          });
          return false;
        }
      }

      if (fieldName === "city" || !fieldName) {
        const city = _formState?.city?.trim();
        if (!city) {
          displayToast({
            type: "error",
            title: "Please enter a city",
          });
          if (!checkErrorStateExists("add_distributor_city", errorState)) {
            updatedErrorState.push({ add_distributor_city: true });
          }
          dispatchErrorStates(updatedErrorState);
          if (!fieldName) {
            focusInputField(updatedErrorState);
          }
          return false;
        } else {
          if (checkErrorStateExists("add_distributor_city", errorState)) {
            updatedErrorState = removeErrorState(
              "add_distributor_city",
              updatedErrorState,
              errorState
            );
            dispatchErrorStates(updatedErrorState);
          }
        }
      }

      if (fieldName === "distributorGstin" || !fieldName) {
        const gstin = _formState?.distributorGstin?.trim();
        const gstinRegex = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/;

        if (!gstin && !gstinRegex.test(gstin)) {
          displayToast({
            type: "error",
            title: "Please enter a valid GSTIN.",
          });
          if (!checkErrorStateExists("add_distributor_gstin", errorState)) {
            updatedErrorState.push({ add_distributor_gstin: true });
          }
          dispatchErrorStates(updatedErrorState);
          if (!fieldName) {
            focusInputField(updatedErrorState);
          }
          return false;
        } else {
          if (checkErrorStateExists("add_distributor_gstin", errorState)) {
            updatedErrorState = removeErrorState(
              "add_distributor_gstin",
              updatedErrorState,
              errorState
            );
            dispatchErrorStates(updatedErrorState);
          }
        }
      }

      if (fieldName === "gstState" || !fieldName) {
        const gstState = _formState?.gstState?.trim();
        if (!gstState) {
          displayToast({
            type: "error",
            title: "Please enter a GST State.",
          });
          if (
            !checkErrorStateExists("add_distributor_state_code", errorState)
          ) {
            updatedErrorState.push({ add_distributor_state_code: true });
          }
          dispatchErrorStates(updatedErrorState);
          if (!fieldName) {
            focusInputField(updatedErrorState);
          }
          return false;
        } else {
          if (checkErrorStateExists("add_distributor_state_code", errorState)) {
            updatedErrorState = removeErrorState(
              "add_distributor_state_code",
              updatedErrorState,
              errorState
            );
            dispatchErrorStates(updatedErrorState);
          }
        }
      }

      if (fieldName === "contact" || !fieldName) {
        if (!_formState?.contact) {
          //contactNumberRef.current?.focus();
          return displayToast({
            type: "error",
            title:
              "Please enter contact number or landline number (General Tab).",
          });
        }
        if (_formState?.contact && !isValidPhoneNumber(_formState.contact)) {
          return displayToast({
            type: "error",
            title: "Please enter a valid 10 digit mobile number (General Tab).",
          });
        }
      }
      if (fieldName === "sendEmail" || !fieldName) {
        const emailRegex = /^\S+@\S+\.\S+$/;
        if (
          _formState.sendEmail?.length &&
          !emailRegex.test(_formState.sendEmail)
        ) {
          displayToast({
            type: "error",
            title: "Please enter a valid sales email address",
          });
          if (
            !checkErrorStateExists("add_distributor_sales_email", errorState)
          ) {
            updatedErrorState.push({
              add_distributor_sales_email: true,
            });
          }
          dispatchErrorStates(updatedErrorState);
          if (!fieldName) {
            focusInputField(updatedErrorState);
          }

          return false;
        }
      }
      if (fieldName === "email" || !fieldName) {
        const emailRegex = /^\S+@\S+\.\S+$/;
        if (_formState.email?.length && !emailRegex.test(_formState.email)) {
          displayToast({
            type: "error",
            title: "Please enter a valid purchase email address",
          });
          if (
            !checkErrorStateExists("add_distributor_purchase_email", errorState)
          ) {
            updatedErrorState.push({
              add_distributor_purchase_email: true,
            });
          }
          dispatchErrorStates(updatedErrorState);
          if (!fieldName) {
            focusInputField(updatedErrorState);
          }

          return false;
        }
      }
      return true;
    },
    [dispatchErrorStates, errorState]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceValidateForm = useCallback(_.debounce(validateForm, 300), []);

  const tabs = useMemo(
    () => [
      {
        type: ADD_DISTRIBUTOR_GENERAL_TAB,
        label: "General",
        showCloseIcon: false,
        props: {
          validateForm: debounceValidateForm,
          removeErrorStateOnChange,
          resetForm,
        },
        endAvatar: <Shortcut keys={["Alt", "1"]} />,
      },
      {
        type: ADD_DISTRIBUTOR_LICENSE_TAB,
        label: "Licenses",
        props: {
          validateForm: debounceValidateForm,
          removeErrorStateOnChange,
          resetForm,
        },
        endAvatar: <Shortcut keys={["Alt", "2"]} />,
      },
      {
        type: ADD_DISTRIBUTOR_ACCOUNT_TAB,
        label: "Account",
        props: {
          validateForm: debounceValidateForm,
          removeErrorStateOnChange,
          resetForm,
        },
        endAvatar: <Shortcut keys={["Alt", "3"]} />,
      },
      {
        type: ADD_DISTRIBUTOR_VISIT_TAB,
        label: "Visits",
        props: {
          validateForm: debounceValidateForm,
          removeErrorStateOnChange,
          resetForm,
        },
        endAvatar: <Shortcut keys={["Alt", "4"]} />,
      },
    ],
    [debounceValidateForm, removeErrorStateOnChange, resetForm]
  );
  const [focusTab, setFocusTab] = useState(0);
  const escapeHandler = useCallback(() => {
    onEscape();
  }, [onEscape]);
  const saveDistributorHandler = useCallback(async () => {
    try {
      setShowLoading(true);
      if (!validateForm(null, formState)) {
        setShowLoading(false);
        return;
      }
      const distributorFormData = createDistributorFormData({ ...formState });
      const response = await saveDistributor(distributorFormData);
      if (response.status === "success") {
        displayToast({
          type: "success",
          title: "Distributor added successfully.",
        });
        onSaveDistributor();
      } else {
        displayToast({
          type: "error",
          title: response,
        });
      }
    } catch (error) {
      console.error("Error saving distributor:", error);
    } finally {
      setShowLoading(false);
    }
  }, [formState, validateForm, onSaveDistributor, setShowLoading]);

  const debounceAndSave = debounce(saveDistributorHandler, 300);

  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      const altKey = event.altKey;
      const ctrlKey = event.ctrlKey;
      if (altKey) {
        if ([1, 2, 3, 4].includes(+key)) {
          setFocusTab(+key - 1);
          return;
        }
      }
      if (ctrlKey) {
        if (key === "s") {
          addDistributorSaveRef?.current.click();
          return;
        }
      }
      if (key === "escape") {
        escapeHandler();
        return;
      }
    },
    [addDistributorSaveRef, escapeHandler]
  );
  useEffect(() => {
    return () => {
      debounceValidateForm.cancel();
      resetForm();
    };
  }, [debounceValidateForm, resetForm]);
  useEffect(() => {
    resetForm();
  }, [resetForm]);
  useEffect(() => {
    document.addEventListener("keyup", keyupListener, false);
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [keyupListener]);

  useEffect(() => {
    if (!distributorRowEditData) return;
    dispatch({
      type: "set_add_distributor_form_state",
      payload: distributorRowEditData,
    });
  }, [distributorRowEditData, dispatch]);

  return (
    <div className={classes["add-distributor-container"]}>
      <div className={classes["add-distributor-header"]}>
        <Typography fontSize={17} fontWeight={700}>
          Distributor Details
        </Typography>
        <Typography fontSize={17} fontWeight={700}>
          Last Saved Distributor: []
        </Typography>
        <div className={classes["add-distributor-header-account-head"]}>
          <Typography fontSize={17} fontWeight={700}>
            Account Head: LIABILITIES/CREDITORS/SUNDRY CREDITORS
          </Typography>
        </div>
      </div>
      <div className={classes["add-distributor-tabs"]}>
        <Tab
          tabs={tabs}
          focusTabIndex={focusTab}
          showCloseIcon={false}
          tabClicked={(index) => {
            setFocusTab(index);
          }}
        />
      </div>
      <div className={classes["add-distributor-button-container"]}>
        <Button
          id="add_distributor_save_button"
          ref={addDistributorSaveRef}
          endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          label="Save Distributor"
          color="violet"
          onFocus={() =>
            setCurrentComponent("sales_edit_distributor_save_button")
          }
          onClick={debounceAndSave}
        />
      </div>
    </div>
  );
};
export default AddDistributor;
